import { Box, CircularProgress, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Camera, TickCircle } from "iconsax-react";
import { useRef } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FloatingTextField, SelectField, SefanoButton } from "@/components/Molecules";
import type { SettingsGeneralProps } from "@/components/Templates/Settings/Settings.template.types";
import * as styles from "./SettingsGeneralPanel.styles";

type SettingsGeneralPanelProps = SettingsGeneralProps;

export function SettingsGeneralPanel({
  form,
  onSubmit,
  logoUrl,
  isLoading,
  isSubmitting,
  isUploadingLogo,
  saved,
  saveError,
  logoError,
  onLogoSelect,
  canEditIban,
  categoryOptions,
  businessTypeOptions,
}: SettingsGeneralPanelProps) {
  const { t } = useTranslation("settings");
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (isLoading) {
    return <CircularProgress size={32} />;
  }

  return (
    <Box component="form" onSubmit={onSubmit} sx={styles.formStack}>
      <Box sx={styles.logoRow}>
        <Box
          component="button"
          type="button"
          sx={styles.logoButton}
          onClick={() => fileInputRef.current?.click()}
        >
          {isUploadingLogo ? (
            <CircularProgress size={24} />
          ) : logoUrl ? (
            <Box component="img" src={logoUrl} alt="" sx={styles.logoImage} />
          ) : (
            <Camera size={26} variant="Bold" color={theme.palette.text.secondary} />
          )}
        </Box>
        <Box>
          <Typography sx={styles.logoHint}>{t("general.logoLabel")}</Typography>
          <Typography
            sx={{
              fontSize: "0.6875rem",
              color: logoError ? "error.main" : "text.secondary",
              mt: 0.25,
            }}
          >
            {logoError ?? t("general.logoHint")}
          </Typography>
        </Box>
        <Box
          component="input"
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          sx={styles.hiddenInput}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) onLogoSelect(file);
            // Let the same file be picked again after a failed upload.
            event.target.value = "";
          }}
        />
      </Box>

      <FloatingTextField
        label={t("general.name")}
        error={Boolean(form.formState.errors.name)}
        helperText={form.formState.errors.name?.message}
        {...form.register("name")}
      />

      <Box sx={styles.twoColumnGrid}>
        <Controller
          name="category"
          control={form.control}
          render={({ field }) => (
            <SelectField
              label={t("general.category")}
              options={categoryOptions.map((option) => ({
                value: option.value,
                label: option.translate ? t(option.label) : option.label,
              }))}
              value={field.value ?? ""}
              onChange={(event) => field.onChange(event.target.value)}
            />
          )}
        />
        <Controller
          name="business_type"
          control={form.control}
          render={({ field }) => (
            <SelectField
              label={t("general.businessType")}
              options={businessTypeOptions.map((option) => ({
                value: option.value,
                label: option.translate ? t(option.label) : option.label,
              }))}
              value={field.value ?? ""}
              onChange={(event) => field.onChange(event.target.value)}
            />
          )}
        />
      </Box>

      <FloatingTextField
        label={t("general.warehouseAddress")}
        {...form.register("warehouse_address")}
      />
      {canEditIban ? (
        <FloatingTextField
          label={t("general.iban")}
          placeholder="IR000000000000000000000000"
          error={Boolean(form.formState.errors.iban)}
          helperText={form.formState.errors.iban?.message ?? t("general.ibanHelper")}
          slotProps={{ htmlInput: { dir: "ltr", maxLength: 32 } }}
          {...form.register("iban")}
        />
      ) : null}
      <FloatingTextField
        label={t("general.description")}
        sx={{ display: { xs: "none", md: "block" } }}
        {...form.register("description")}
      />
      <FloatingTextField
        label={t("general.mobileDescription")}
        sx={{ display: { xs: "block", md: "none" } }}
        {...form.register("description")}
      />

      {saveError ? (
        <Typography role="alert" sx={{ fontSize: "0.75rem", fontWeight: 600, color: "error.main" }}>
          {saveError}
        </Typography>
      ) : null}

      <Box sx={styles.footerRow}>
        <SefanoButton
          type="submit"
          disabled={isSubmitting || isUploadingLogo}
          sx={{ width: { xs: "100%", md: 150 } }}
        >
          {isSubmitting ? t("saving") : t("general.save")}
        </SefanoButton>
        {saved ? (
          <Box component="span" sx={styles.savedFlash}>
            <TickCircle size={16} variant="Bold" color={theme.palette.success.main} />
            {t("saved")}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
